import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { bffRoutes, formicRequestHeaders, requireSuccessfulBffResponse } from '../bffRoutes';
import { WEB_PLUGIN_CONTRACT_VERSION, WebPluginCatalogue } from './contracts';
import { installWebPluginRegistrationApi } from './registry';

interface PluginContextValue {
  catalogue: WebPluginCatalogue;
  loading: boolean;
  error?: Error;
  refreshCatalogue: () => void;
}

interface PluginLoadState extends Omit<PluginContextValue, 'refreshCatalogue'> {
  loadedPath?: string;
}

const emptyCatalogue: WebPluginCatalogue = {
  contractVersion: WEB_PLUGIN_CONTRACT_VERSION,
  revision: 'none',
  plugins: [],
  problems: [],
};

const PluginContext = createContext<PluginContextValue>({
  catalogue: emptyCatalogue,
  loading: false,
  refreshCatalogue: () => undefined,
});

export const WebPluginProvider: FC = ({ children }) => {
  const { pathname } = useLocation();
  const [value, setValue] = useState<PluginLoadState>({
    catalogue: emptyCatalogue,
    loading: true,
  });
  const [refreshNumber, setRefreshNumber] = useState(0);
  const refreshCatalogue = useCallback(() => setRefreshNumber((current) => current + 1), []);

  useEffect(() => {
    window.addEventListener('focus', refreshCatalogue);
    return () => window.removeEventListener('focus', refreshCatalogue);
  }, [refreshCatalogue]);

  useEffect(() => {
    installWebPluginRegistrationApi();
    const abort = new AbortController();
    setValue((current) => ({ ...current, error: undefined }));

    Promise.resolve()
      .then(() =>
        fetch(bffRoutes.plugins, {
          method: 'GET',
          headers: formicRequestHeaders,
          credentials: 'same-origin',
          signal: abort.signal,
        }),
      )
      .then((response) => requireSuccessfulBffResponse(response, 'Web plugin catalogue'))
      .then((response) => response.json())
      .then((catalogue: WebPluginCatalogue) => {
        if (catalogue.contractVersion !== WEB_PLUGIN_CONTRACT_VERSION)
          throw new Error('PLUGIN_CONTRACT_VERSION_UNSUPPORTED');
        if (!Array.isArray(catalogue.plugins) || !Array.isArray(catalogue.problems))
          throw new Error('PLUGIN_CATALOGUE_INVALID');

        catalogue.problems.forEach((problem) =>
          console.warn(
            '[OF2 plugin]',
            problem.code,
            problem.pluginId ?? problem.applicationId ?? '',
          ),
        );
        setValue({ catalogue, loading: false, loadedPath: pathname });
      })
      .catch((error: unknown) => {
        if (abort.signal.aborted) return;
        console.error('[OF2 plugin] catalogue failed');
        setValue({
          catalogue: emptyCatalogue,
          loading: false,
          loadedPath: pathname,
          error: error instanceof Error ? error : new Error('PLUGIN_CATALOGUE_FAILED'),
        });
      });

    return () => abort.abort();
  }, [pathname, refreshNumber]);

  const context = useMemo(
    () => ({
      catalogue: value.catalogue,
      loading: value.loading || value.loadedPath !== pathname,
      error: value.error,
      refreshCatalogue,
    }),
    [pathname, refreshCatalogue, value],
  );
  return <PluginContext.Provider value={context}>{children}</PluginContext.Provider>;
};

export const useWebPlugins = (): PluginContextValue => useContext(PluginContext);
